import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { User, Mail, Lock, ArrowLeft, Loader2, AlertCircle, UserPlus } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Input } from '../../Components/ui';
import { API_URL } from '../../api/config';

// Tela de autocadastro. Usuários comuns entram "pendentes" até um master aprovar
// (ver POST /api/auth/register no backend); só o primeiro master do sistema entra
// direto logado — por isso a opção "Master" só aparece quando `hasMaster` é falso.
function Registrar() {
  const navigate = useNavigate();
  const { register, hasMaster } = useAuth();
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [confirmarSenha, setConfirmarSenha] = useState('');
  const [perfil, setPerfil] = useState('monitor');
  const [instituicaoId, setInstituicaoId] = useState('');
  const [instituicoes, setInstituicoes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState('');

  useEffect(() => {
    fetch(`${API_URL}/api/auth/instituicoes`)
      .then((res) => res.json())
      .then((data) => {
        const list = data?.data ?? (Array.isArray(data) ? data : []);
        setInstituicoes(Array.isArray(list) ? list : []);
      })
      .catch(() => setInstituicoes([]));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');

    if (senha !== confirmarSenha) {
      setErro('As senhas não conferem.');
      return;
    }

    if (perfil !== 'master' && !instituicaoId) {
      setErro('Selecione a instituição vinculada ao usuário.');
      return;
    }

    setLoading(true);
    try {
      const response = await register(nome, email, senha, perfil, instituicaoId || null);
      if (response.token && response.user) {
        navigate('/selecionar', { replace: true });
      } else {
        navigate('/login', {
          state: {
            mensagem: response.message || 'Cadastro realizado. Aguarde aprovação do master.',
          },
        });
      }
    } catch (error) {
      setErro(error.response?.data?.error || 'Não foi possível criar o registro.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-br from-[#F9FAFB] to-[#E5E7EB] dark:from-gray-950 dark:to-gray-900 p-4">
      <div className="w-full max-w-md bg-white/80 dark:bg-gray-900/80 backdrop-blur-xl rounded-3xl border border-white/50 dark:border-gray-800/50 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.1)] p-4">
        <div className="flex flex-col items-center mb-6">
          <img src="/logo.svg" alt="Portal Novas Histórias" className="w-16 h-auto object-contain mb-4" />
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100 tracking-tight">
            Criar registro
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Cadastre seu usuário para acessar o sistema
          </p>
        </div>

        {erro && (
          <div className="mb-4 p-3 rounded-xl bg-red-50 dark:bg-red-900/20 border border-red-100 dark:border-red-900/40 text-red-700 dark:text-red-400 text-sm flex items-start gap-2">
            <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            {erro}
          </div>
        )}

        <form className="space-y-4" onSubmit={handleSubmit}>
          <Input
            label="Nome"
            size="lg"
            value={nome}
            onChange={(e) => setNome(e.target.value)}
            required
            autoComplete="name"
            icon={User}
          />
          <Input
            type="email"
            label="E-mail"
            size="lg"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            autoComplete="email"
            icon={Mail}
          />
          <div className="space-y-1.5">
            <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
              Perfil
            </label>
            <select
              value={perfil}
              onChange={(e) => setPerfil(e.target.value)}
              required
              className="w-full h-11 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none"
            >
              <option value="monitor">Monitor</option>
              <option value="professor">Educador</option>
              <option value="coordenador">Coordenador</option>
              {!hasMaster && <option value="master">Master</option>}
            </select>
          </div>
          {perfil !== 'master' && (
            <div className="space-y-1.5">
              <label className="block text-xs font-medium text-gray-500 dark:text-gray-400">
                Instituição vinculada
              </label>
              <select
                value={instituicaoId}
                onChange={(e) => setInstituicaoId(e.target.value)}
                required
                className="w-full h-11 px-3 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-sm text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all appearance-none"
              >
                <option value="">Selecione...</option>
                {instituicoes.map((inst) => (
                  <option key={inst.id} value={inst.id}>
                    {inst.nome}
                  </option>
                ))}
              </select>
            </div>
          )}
          <Input
            type="password"
            label="Senha"
            size="lg"
            value={senha}
            onChange={(e) => setSenha(e.target.value)}
            required
            minLength="6"
            autoComplete="new-password"
            icon={Lock}
          />
          <Input
            type="password"
            label="Confirmar senha"
            size="lg"
            value={confirmarSenha}
            onChange={(e) => setConfirmarSenha(e.target.value)}
            required
            minLength="6"
            autoComplete="new-password"
            icon={Lock}
          />

          <button
            type="submit"
            disabled={loading}
            className="w-full h-11 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium transition-all disabled:opacity-60 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {loading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <UserPlus className="w-4 h-4" />
            )}
            {loading ? 'Criando...' : 'Criar conta'}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          Já tem conta?{' '}
          <Link
            to="/login"
            className="text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
          >
            Entrar
          </Link>
        </div>
        <div className="mt-3 text-center">
          <button
            onClick={() => navigate(-1)}
            className="text-sm text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 transition-colors flex items-center justify-center gap-1 mx-auto"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Voltar
          </button>
        </div>
      </div>
    </div>
  );
}

export default Registrar;
